/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { Else, If, Then } from "react-if";
import { theme } from "@nordcloud/gnui";
import {
  BrickLoader,
  UniversalWrap,
  mapAccumulatedCostChartColors,
  ChartType,
  Granularity,
  EstatePeriod,
  Wrapper,
  DateRange,
  LoaderWrap,
} from "~/components";
import { useCurrency } from "~/hooks";
import { InputError } from "~/tools";
import { getEstateTotalsTableColumns, EstateCostTable } from "../Views";
import { AccumulatedCostData } from "./AccumulatedCostData";
import { EstateRecordsGroupingChart } from "./EstateRecordsGroupingChart";
import { getAverageCost } from "./helpers";
import { useEstateRecordsAccumulatedCost } from "./hooks";
import { getCategories } from "./hooks/helpers";
import { ChartJobData } from "./types";

type Props = {
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: EstatePeriod;
  loading: boolean;
  isCostLoading: boolean;
  estateRecordsCount: number;
  range?: DateRange;
  chartData?: ChartJobData;
  error: InputError | undefined;
  currentMonthTotal?: string;
  monthlySavings?: string | null;
};

export function EstateRecordsAccumulatedCostChart({
  startDate,
  endDate,
  granularity,
  period,
  range,
  loading,
  chartData,
  error,
  isCostLoading,
  estateRecordsCount,
  currentMonthTotal,
  monthlySavings,
}: Props) {
  const { currency } = useCurrency();

  const {
    accumulatedCostChartData,
    accumulatedCostTableData,
    accumulatedCostTotal,
  } = useEstateRecordsAccumulatedCost(granularity, chartData);

  const costCategories = getCategories(accumulatedCostChartData);

  const colors = mapAccumulatedCostChartColors(costCategories);

  const columns = useMemo(
    () =>
      getEstateTotalsTableColumns({
        startDate,
        endDate,
        currency,
        data: accumulatedCostTableData,
        period,
        colors,
        chartType: ChartType.ACCUMULATED_COST,
      }),
    [startDate, endDate, currency, accumulatedCostTableData, period, colors]
  );

  return (
    <Wrapper
      css={{ margin: theme.spacing.spacing02 }}
      data-testid="accumulated-cost-chart-wrapper"
    >
      <UniversalWrap
        loaderProps={{ loading, Component: BrickLoader }}
        errorProps={{ error }}
      >
        <EstateRecordsGroupingChart
          data={accumulatedCostChartData}
          granularity={granularity}
          loading={loading}
          startDate={startDate}
          endDate={endDate}
          period={period}
          colors={colors}
          keys={costCategories}
          chartType={ChartType.ACCUMULATED_COST}
        />
      </UniversalWrap>
      <If condition={period === EstatePeriod.RANGE}>
        <Then>
          <AccumulatedCostData
            period={period}
            range={range}
            totalCost={accumulatedCostTotal}
            isLoading={loading}
            averageCost={getAverageCost(
              accumulatedCostTotal,
              startDate,
              endDate
            )}
          />
          <div css={{ width: "100%", overflow: "scroll" }}>
            <LoaderWrap loading={loading} Component={BrickLoader}>
              <EstateCostTable
                data={accumulatedCostTableData}
                columns={columns}
                period={EstatePeriod.DEFAULT_MONTH}
              />
            </LoaderWrap>
          </div>
        </Then>
        <Else>
          <AccumulatedCostData
            period={period}
            range={range}
            isLoading={isCostLoading}
            totalCost={currentMonthTotal}
            monthlySavings={monthlySavings}
            count={estateRecordsCount}
          />
        </Else>
      </If>
    </Wrapper>
  );
}
